import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { HelpResponse } from '../../api/help';
import type { DebriefResponse } from '../../hooks/useAssessmentApi';
import type { LessonPart, ResumeOffer } from '../../hooks/useLessonApi';
import type { Message } from '../../hooks/useLessonState';
import type { STTError } from '../../speech';
import { buildRail, type EarlierPhrase } from '../../turncard/rail';
import {
  useTurnShortcuts,
  type ShortcutPhase,
} from '../../turncard/useTurnShortcuts';
import { buildTurnViewModel, type RunMode } from '../../turncard/viewModel';
import type { PracticeSummary } from '../../api/practice';
import AdvanceRetry from '../lesson/AdvanceRetry';
import ErrorBanner from '../ui/ErrorBanner';
import LoadingState from '../ui/LoadingState';
import SilentModeHint from '../ui/SilentModeHint';
import type { MicTranscript } from '../ui/MicButton';
import { DebriefCards } from './DebriefCards';
import { IntroCard } from './IntroCard';
import { LeaveConfirm } from './LeaveConfirm';
import { PartsStrip } from './PartsStrip';
import { PauseOverlay } from './PauseOverlay';
import { ResumeCard } from './ResumeCard';
import { Rail } from './Rail';
import { TopBar } from './TopBar';
import { TurnCard } from './TurnCard';
import styles from './TurnCardView.module.css';

export interface TurnCardViewProps {
  mode: RunMode;
  loading: boolean;
  processing: boolean;
  error: string | null;
  messages: Message[];
  /** Phrases learned before this run was resumed (#347); they are not on screen as messages. */
  earlierPhrases?: EarlierPhrase[];
  parts: LessonPart[];
  contextName: string | null;
  lessonComplete: boolean;
  loadingMessage?: string;
  onRetry: () => void;
  onBackFromError?: () => void;
  /** Leave for Today (no confirmation here; the view asks mid-session). */
  onLeave: () => void;
  // Audio and mic: the same callbacks the chat view takes
  onStartRecording: () => void;
  onStopRecording: () => void;
  onTranscript: (result: MicTranscript) => void;
  onSttError?: (error: STTError) => void;
  languageCode?: string;
  phraseHints?: string[] | null;
  runId?: string | null;
  stepIndex?: number | null;
  onAudioEnded?: (messageId: string) => void;
  currentPhraseId?: string | null;
  onRetryAudio?: (messageId: string) => Promise<boolean>;
  onSkipAudio?: (messageId: string) => void;
  onAskHelp?: (formId: string | null) => Promise<HelpResponse>;
  awaitingInput: boolean;
  advanceFailed?: boolean;
  onRetryAdvance?: () => void;
  /** "I need help": the next level of help for the phrase to say (lessons and practice; BR-052). */
  onNeedHelp?: () => void;
  /** An unfinished run to pick up; shown instead of the intro until the learner chooses. */
  resumeOffer?: ResumeOffer | null;
  /** The run on screen was resumed: the learner already saw its intro. */
  resumed?: boolean;
  onContinue?: () => void;
  onRestart?: () => void;
  // Debrief
  debrief: DebriefResponse | null;
  debriefLoading: boolean;
  practiceSummary?: PracticeSummary | null;
  onStartCheckIn?: () => void;
  onNextLesson?: () => void;
  nextLessonStarting?: boolean;
}

const sceneTextOf = (messages: Message[]): string =>
  messages.find(
    (m) =>
      m.stepKind === 'provide_context' || m.stepKind === 'provide_introduction'
  )?.content ?? '';

/**
 * The turn card: one turn on screen at a time (#306). Presentational, like `LessonRunnerView`: the
 * runners own the flow, the audio and the mic callbacks; this only arranges what the view model says.
 * Pausing, or asking to leave, unmounts the card, which stops its clip and cancels any recording.
 */
export const TurnCardView: React.FC<TurnCardViewProps> = (props) => {
  const {
    mode,
    loading,
    processing,
    error,
    messages,
    earlierPhrases,
    parts,
    contextName,
    lessonComplete,
    loadingMessage,
    onRetry,
    onBackFromError,
    onLeave,
    currentPhraseId,
    awaitingInput,
    advanceFailed,
    onRetryAdvance,
  } = props;
  const [started, setStarted] = useState(false);
  // Check-in lines heard (or unplayable) this run; a new run starts clean.
  const [heardIds, setHeardIds] = useState<ReadonlySet<string>>(new Set());
  const [paused, setPaused] = useState(false);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const startedRunRef = useRef<string | null>(null);
  // A new run starts at its intro again; a resumed one skips it.
  if (startedRunRef.current !== (props.runId ?? null)) {
    startedRunRef.current = props.runId ?? null;
    if (started !== !!props.resumed) setStarted(!!props.resumed);
    if (heardIds.size > 0) setHeardIds(new Set());
  }

  // Recording is tracked here only to keep R, S and the rail's replay quiet while the learner speaks.
  const [recording, setRecording] = useState(false);
  useEffect(() => {
    if (!awaitingInput) setRecording(false);
  }, [awaitingInput]);

  const vm = useMemo(
    () =>
      buildTurnViewModel({
        mode,
        messages,
        parts,
        awaitingInput,
        processing,
        currentPhraseId: currentPhraseId ?? null,
        heardIds,
      }),
    [
      mode,
      messages,
      parts,
      awaitingInput,
      processing,
      currentPhraseId,
      heardIds,
    ]
  );

  const phase: ShortcutPhase =
    loading || error || lessonComplete
      ? 'off'
      : !started
        ? 'intro'
        : paused || confirmingLeave
          ? 'off'
          : 'card';
  useTurnShortcuts({
    phase,
    busy: recording || processing,
    onPause: () => {
      setRecording(false);
      setPaused(true);
    },
  });

  const rail = useMemo(
    () =>
      buildRail({
        mode,
        messages,
        parts,
        activePartId: vm.partId,
        earlier: earlierPhrases,
      }),
    [mode, messages, parts, vm.partId, earlierPhrases]
  );

  if (loading) {
    return (
      <LoadingState
        variant="full-page"
        message={loadingMessage ?? 'Starting lesson...'}
      />
    );
  }
  if (error) {
    return (
      <ErrorBanner
        message={error}
        variant="full-page"
        onRetry={onRetry}
        onBack={onBackFromError}
      />
    );
  }

  const title = contextName ?? '';

  if (props.resumeOffer && props.onContinue && props.onRestart) {
    return (
      <div className={styles.page}>
        <main className={styles.main}>
          <ResumeCard
            mode={mode}
            offer={props.resumeOffer}
            onContinue={props.onContinue}
            onRestart={props.onRestart}
          />
        </main>
      </div>
    );
  }

  if (lessonComplete) {
    return (
      <div className={styles.page}>
        <TopBar
          title={title}
          onPause={() => undefined}
          onLeave={onLeave}
          pauseDisabled
        />
        <main className={styles.main}>
          <DebriefCards
            mode={mode}
            debrief={props.debrief}
            debriefLoading={props.debriefLoading}
            practiceSummary={props.practiceSummary ?? null}
            onBackToToday={onLeave}
            onStartCheckIn={props.onStartCheckIn}
            onNextLesson={props.onNextLesson}
            nextLessonStarting={props.nextLessonStarting}
          />
        </main>
      </div>
    );
  }

  if (!started) {
    return (
      <div className={styles.page}>
        <main className={styles.main}>
          <IntroCard
            contextName={title}
            sceneText={sceneTextOf(messages)}
            parts={parts}
            mode={mode}
            ready={messages.length > 0}
            onStart={() => setStarted(true)}
            onLeave={onLeave}
          />
        </main>
      </div>
    );
  }

  const contextAudios = messages.filter((m) =>
    vm.contexts.some((c) => c.messageId === m.id)
  );

  const stopRecording = (): void => {
    setRecording(false);
    props.onStopRecording();
  };

  return (
    <div className={styles.shell}>
      <div className={`${styles.page} ${rail.isEmpty ? '' : styles.hasRail}`}>
        <TopBar
          title={title}
          onPause={() => {
            setRecording(false);
            setPaused(true);
          }}
          onLeave={() => {
            setRecording(false);
            setConfirmingLeave(true);
          }}
        />
        <div className={styles.stripOnly}>
          <PartsStrip strip={vm.strip} />
        </div>
        <div className={styles.body}>
          {!rail.isEmpty && (
            <div className={styles.railSlot}>
              <Rail rail={rail} disabled={recording || processing} />
            </div>
          )}
          <main className={styles.main}>
            <SilentModeHint />
            {paused || confirmingLeave ? null : (
              <TurnCard
                mode={mode}
                vm={vm}
                processing={processing}
                paused={false}
                onAudioEnded={props.onAudioEnded}
                onRetryAudio={props.onRetryAudio}
                onSkipAudio={props.onSkipAudio}
                onAskHelp={props.onAskHelp}
                onLineHeard={(id) =>
                  setHeardIds((prev) => new Set(prev).add(id))
                }
                onStartRecording={() => {
                  setRecording(true);
                  props.onStartRecording();
                }}
                onStopRecording={stopRecording}
                onTranscript={(result) => {
                  setRecording(false);
                  props.onTranscript(result);
                }}
                onSttError={(err) => {
                  setRecording(false);
                  props.onSttError?.(err);
                }}
                languageCode={props.languageCode}
                phraseHints={props.phraseHints}
                runId={props.runId}
                stepIndex={props.stepIndex}
                awaitingInput={awaitingInput}
                onNeedHelp={props.onNeedHelp}
                contextAudios={contextAudios}
              />
            )}
            {advanceFailed && onRetryAdvance && (
              <div className={styles.retry}>
                <AdvanceRetry onRetry={onRetryAdvance} disabled={processing} />
              </div>
            )}
          </main>
        </div>
        {paused && <PauseOverlay onResume={() => setPaused(false)} />}
        {confirmingLeave && (
          <LeaveConfirm
            onStay={() => setConfirmingLeave(false)}
            onLeave={onLeave}
          />
        )}
      </div>
    </div>
  );
};

export default TurnCardView;
